import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
  Type,
} from '@angular/core';
import { MODAL_REF, ModalRef } from './modal-ref.token';

export interface PopoverOptions {
  component: Type<unknown>;
  event?: Event;
  componentProps?: Record<string, unknown>;
  cssClass?: string;
  showBackdrop?: boolean;
}

@Injectable({ providedIn: 'root' })
export class PopoverService {
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private envInjector: EnvironmentInjector,
  ) {}

  async create(options: PopoverOptions): Promise<{ present(): Promise<void>; onDidDismiss(): Promise<{ data: unknown }> }> {
    let resolvePromise: (value: { data: unknown }) => void;
    const dismissPromise = new Promise<{ data: unknown }>((resolve) => {
      resolvePromise = resolve;
    });

    const dismiss = (data?: unknown) => {
      this.destroyPopover(backdrop, container, componentRef);
      resolvePromise({ data });
    };

    const backdrop = document.createElement('div');
    backdrop.className = 'popover-backdrop';
    if (options.showBackdrop === false) {
      backdrop.style.background = 'transparent';
    }
    backdrop.addEventListener('click', () => dismiss(undefined));

    const container = document.createElement('div');
    container.className = 'popover-container';
    if (options.cssClass) {
      options.cssClass.split(' ').forEach(c => container.classList.add(c));
    }

    const modalRef: ModalRef = { dismiss };

    const childInjector = Injector.create({
      providers: [{ provide: MODAL_REF, useValue: modalRef }],
      parent: this.injector,
    });

    const componentRef = createComponent(options.component, {
      environmentInjector: this.envInjector,
      elementInjector: childInjector,
      hostElement: container,
    });

    if (options.componentProps) {
      Object.entries(options.componentProps).forEach(([key, value]) => {
        (componentRef.instance as Record<string, unknown>)[key] = value;
      });
    }

    this.appRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();

    // Position near the trigger event
    if (options.event) {
      const target = options.event.target as HTMLElement;
      if (target) {
        const rect = target.getBoundingClientRect();
        container.style.position = 'fixed';
        container.style.zIndex = '10002';
        container.style.left = `${rect.left}px`;
        // Temporarily add to DOM off-screen to measure size
        container.style.visibility = 'hidden';
        container.style.top = '0px';
        document.body.appendChild(container);
        const popoverHeight = container.offsetHeight;
        const popoverWidth = container.offsetWidth;
        container.remove();
        container.style.visibility = '';

        const margin = 4;

        // Vertical: anchor based on where the trigger sits in the viewport.
        // If the trigger is in the upper half, open downward; if it's in the
        // lower half, open upward. This avoids popovers spilling off the
        // bottom for messages near the input bar.
        const triggerCenterY = rect.top + rect.height / 2;
        const openDown = triggerCenterY < window.innerHeight / 2;
        let top = openDown
          ? rect.bottom + margin
          : rect.top - popoverHeight - margin;

        // Horizontal: prefer aligned to trigger's left; if that would overflow
        // the right edge, shift left so the popover sits flush to the right.
        let left = rect.left;
        if (left + popoverWidth > window.innerWidth - margin) {
          left = window.innerWidth - popoverWidth - margin;
        }

        // Final clamp so the popover always stays inside the viewport even
        // when the trigger is near a corner or the popover is very large.
        top = Math.max(margin, Math.min(top, window.innerHeight - popoverHeight - margin));
        left = Math.max(margin, Math.min(left, window.innerWidth - popoverWidth - margin));

        container.style.top = `${top}px`;
        container.style.left = `${left}px`;
      }
    }

    return {
      present: async () => {
        document.body.appendChild(backdrop);
        document.body.appendChild(container);
      },
      onDidDismiss: () => dismissPromise,
    };
  }

  private destroyPopover(
    backdrop: HTMLElement,
    container: HTMLElement,
    componentRef: ComponentRef<unknown>,
  ): void {
    this.appRef.detachView(componentRef.hostView);
    componentRef.destroy();
    backdrop.remove();
    container.remove();
  }
}
